<template lang="pug">
el-drawer(
  title="可排案時段設定"
  :visible.sync="drawer"
  direction="rtl"
  :size="branchEditable ? '800px' : '640px'"
  :wrapperClosable="false"
  :before-close="handleClose"
  :append-to-body="appendToBody")
  .drawer.flex.column.gap-20.default
    .update-info.text-secondary(v-if="updateText") {{ updateText }}
    .data.flex.align-center.gap-10
      el-switch(v-model="holidaysAvailable")
      span 國定假日照常（開啟後，國定假日依每週時段判斷，不視為整天不可排案）
    .data.flex.column.align-stretch.gap-10
      .title 每週可排案時段
      .empty-hint.text-secondary(v-if="!hasAnySlot") 未設定時段將視為全時段可排案
      .day.flex.align-start.gap-10(v-for="(slots, idx) in weekly", :key="idx")
        .day-name {{ weekdayNames[idx + 1] }}
        .slots.flex.column.align-stretch.gap-6.flex-1
          .text-placeholder.empty-day(v-if="slots.length === 0") 無可排案時段
          .slot.flex.align-center.gap-6(v-for="(slot, sIdx) in slots", :key="slot.key")
            el-time-select.time(
              v-model="slot.start"
              :clearable="false"
              placeholder="起"
              :picker-options="startOptions")
            span 至
            el-time-select.time(
              v-model="slot.end"
              :clearable="false"
              placeholder="訖"
              :picker-options="endOptions(slot)")
            el-select.branch(
              v-if="branchEditable"
              v-model="slot.branchIds"
              multiple
              collapse-tags
              placeholder="未指定館別")
              el-option-group(
                v-for="group in branchGroups"
                :key="group.label"
                :label="group.label")
                el-option(
                  v-for="b in group.branches"
                  :key="b.id"
                  :label="b.name"
                  :value="b.id")
            el-button(
              type="text" icon="el-icon-delete" @click="removeSlot(idx, sIdx)")
        .day-ops.flex.align-center.gap-6
          el-button(type="text" icon="el-icon-plus" @click="addSlot(idx)") 新增
          el-dropdown(
            trigger="click"
            :disabled="slots.length === 0"
            @command="copyDay(idx, $event)")
            el-button(type="text" :disabled="slots.length === 0") 複製到
            el-dropdown-menu(slot="dropdown")
              el-dropdown-item(command="all") 其他所有天（覆蓋）
              el-dropdown-item(
                v-for="target in copyTargets(idx)"
                :key="target"
                :command="target") {{ weekdayNames[target + 1] }}（覆蓋）
    .data.flex.column.align-stretch.gap-10
      .title.flex.align-center.gap-10
        span 不可排案日期
        el-checkbox(v-model="showPast" v-if="pastCount > 0") 顯示過去的日期（{{ pastCount }}）
      .text-secondary 整天：該日期整天不可排案，優先於每週時段；指定時段：只限單日，可加多個時段
      .off.flex.column.align-stretch.gap-6(v-for="entry in visibleUnavailable", :key="entry.key")
        .flex.align-center.gap-6
          el-radio-group(v-model="entry.partial" size="small" @change="changePartial(entry)")
            el-radio-button(:label="false") 整天
            el-radio-button(:label="true") 指定時段
          el-date-picker.date(
            v-if="entry.partial"
            :value="entry.range[0]"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="日期"
            :clearable="false"
            @input="setSingleDate(entry, $event)")
          el-date-picker.range(
            v-else
            v-model="entry.range"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="起日"
            end-placeholder="迄日"
            :clearable="false"
            @change="sortUnavailable")
          .flex-1
          el-button(
            type="text" icon="el-icon-delete" @click="removeUnavailable(entry)")
        template(v-if="entry.partial")
          .off-slot.flex.align-center.gap-6(v-for="slot in entry.slots", :key="slot.key")
            el-time-select.time(
              v-model="slot.start"
              :clearable="false"
              placeholder="起"
              :picker-options="startOptions")
            span 至
            el-time-select.time(
              v-model="slot.end"
              :clearable="false"
              placeholder="訖"
              :picker-options="endOptions(slot)")
            el-select.reason(v-model="slot.reason" placeholder="原因（必填）")
              el-option(
                v-for="r in reasonOptions"
                :key="r.value"
                :label="r.label"
                :value="r.value")
            el-button(
              type="text" icon="el-icon-close"
              :disabled="entry.slots.length <= 1"
              @click="removeOffSlot(entry, slot)")
          el-button.add-off-slot(
            type="text" icon="el-icon-plus" @click="addOffSlot(entry)") 新增時段
        .flex.align-center.gap-6(v-else)
          el-select.reason(v-model="entry.reason" placeholder="原因（必填）")
            el-option(
              v-for="r in reasonOptions"
              :key="r.value"
              :label="r.label"
              :value="r.value")
      el-button.add-off(size="small" icon="el-icon-plus" @click="addUnavailable") 新增不可排案日期
    .op.flex.align-center-justify-stretch.gap-10
      el-button.flex-1(@click="cancel") 取消
      el-button.flex-1(type="primary" :disabled="!loaded" @click="save") 儲存
</template>

<script>
import msg from '@/util/msg';
import {
  describeUpdatedBy,
  minuteToTime,
  timeToMinute,
  toDateKey,
} from '@/util/availability';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

// 依起日、迄日排序；同日時整天在前
function sortKey(u) {
  const range = u.range || ['', ''];
  return `${range[0] || ''} ${range[1] || ''} ${u.partial ? 1 : 0}`;
}

function compareUnavailable(a, b) {
  return sortKey(a).localeCompare(sortKey(b));
}

function compareSlotStart(a, b) {
  return (a.start || '').localeCompare(b.start || '');
}

export default {
  name: 'professional-availability-editor',
  props: {
    // () => Promise<availability>
    loader: {
      type: Function,
      required: true,
    },
    // (payload) => Promise<availability & { conflicts }>；payload 含 version
    saver: {
      type: Function,
      required: true,
    },
    // () => Promise<{ branches: [{ id, name }], registeredIds: number[] }>；
    // branches 為所有可選館別，registeredIds 為專業人士的登記館別。
    // 沒傳時不顯示館別選單，但每週時段的 branch_ids 會原樣送回
    branchLoader: {
      type: Function,
      default: null,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      drawer: false,
      loaded: false,
      version: 0,
      updatedAt: null,
      updatedBy: null,
      holidaysAvailable: false,
      // weekly[0..6] 對應週一到週日，每項 { key, start, end, branchIds }
      weekly: WEEKDAYS.map(() => []),
      // 館別選項，載入 branchLoader 後才有值
      branches: [],
      registeredBranchIds: [],
      branchEditable: false,
      // 含過去的所有不可排案日期，每項 { key, range: [start, end], partial, reason, slots }
      // partial 為 false 時整天不可排案，原因為 reason；
      // true 時只限單日（range 起訖相同），slots 為當天的多個時段 [{ key, start, end, reason }]，"HH:MM"。
      // 後端是一段一筆，載入時同一天的指定時段合成一筆，送出時再拆開
      unavailable: [],
      showPast: false,
      keySeed: 0,
      weekdayNames: msg.availability.weekdayNames,
      reasonOptions: Object.entries(msg.availability.reasons).map(([value, label]) => ({ value, label })),
      startOptions: { start: '00:00', step: '00:30', end: '23:30' },
    };
  },
  computed: {
    todayKey() {
      return toDateKey(new Date());
    },
    hasAnySlot() {
      return this.weekly.some((slots) => slots.length > 0);
    },
    updateText() {
      if (!this.updatedAt) {
        return '';
      }
      return [`最後更新：${this.$timestampToDateTimeMinute(this.updatedAt)}`, describeUpdatedBy(this.updatedBy)].filter(Boolean).join('，');
    },
    pastCount() {
      return this.unavailable.filter((u) => this.isPast(u)).length;
    },
    visibleUnavailable() {
      return this.unavailable.filter((u) => this.showPast || !this.isPast(u));
    },
    // 登記館別在前；已存的 branch_ids 若不在館別清單中（例如已刪除），補在其他館別避免被吃掉
    branchGroups() {
      const byId = {};
      this.branches.forEach((b) => {
        byId[b.id] = b;
      });
      const registered = this.registeredBranchIds.map((id) => byId[id] || { id, name: `館別 #${id}` });
      const known = new Set(this.registeredBranchIds);
      const others = this.branches.filter((b) => !known.has(b.id));
      others.forEach((b) => known.add(b.id));
      this.weekly.forEach((slots) => slots.forEach((s) => s.branchIds.forEach((id) => {
        if (!known.has(id)) {
          known.add(id);
          others.push({ id, name: `館別 #${id}` });
        }
      })));
      return [
        { label: '登記館別', branches: registered },
        { label: '其他館別', branches: others },
      ].filter((g) => g.branches.length > 0);
    },
    // 館別 id → 選單中的順序，送出時依此排序
    branchOrder() {
      const order = {};
      this.branchGroups
        .flatMap((g) => g.branches)
        .forEach((b, i) => {
          order[b.id] = i;
        });
      return order;
    },
  },
  methods: {
    nextKey() {
      this.keySeed += 1;
      return this.keySeed;
    },
    isPast(entry) {
      return !!entry.range && entry.range[1] < this.todayKey;
    },
    // slot 為 { start } 的每週時段或部分不可排案時段
    endOptions(slot) {
      const options = { start: '00:30', step: '00:30', end: '24:00' };
      if (slot.start) {
        options.minTime = slot.start;
      }
      return options;
    },
    copyTargets(idx) {
      return WEEKDAYS.map((w, i) => i).filter((i) => i !== idx);
    },
    addSlot(idx) {
      const slots = this.weekly[idx];
      const last = slots[slots.length - 1];
      let start = '09:00';
      let end = '12:00';
      if (last && last.end && timeToMinute(last.end) < 1380) {
        const startMin = timeToMinute(last.end);
        start = minuteToTime(startMin);
        end = minuteToTime(Math.min(startMin + 180, 1440));
      }
      // 館別沿用同一天的上一段，沒有就預設登記館別
      const branchIds = last ? [...last.branchIds] : [...this.registeredBranchIds];
      slots.push({
        key: this.nextKey(), start, end, branchIds,
      });
    },
    removeSlot(idx, sIdx) {
      this.weekly[idx].splice(sIdx, 1);
    },
    copyDay(idx, target) {
      const source = this.weekly[idx];
      const targets = target === 'all' ? this.copyTargets(idx) : [target];
      targets.forEach((t) => {
        this.$set(this.weekly, t, source.map((s) => ({
          key: this.nextKey(), start: s.start, end: s.end, branchIds: [...s.branchIds],
        })));
      });
    },
    newOffSlot(start, end, reason) {
      return {
        key: this.nextKey(), start, end, reason,
      };
    },
    addUnavailable() {
      this.unavailable.push({
        key: this.nextKey(),
        range: [this.todayKey, this.todayKey],
        partial: false,
        reason: '',
        slots: [],
      });
    },
    changePartial(entry) {
      if (entry.partial) {
        // 指定時段只限單日，改用起日；切回整天時保留 slots，再切回來不會遺失
        const date = entry.range && entry.range[0];
        Object.assign(entry, { range: [date, date] });
        if (entry.slots.length === 0) {
          entry.slots.push(this.newOffSlot('09:00', '12:00', entry.reason));
        }
        this.mergeSameDate(entry);
      } else if (!entry.reason && entry.slots[0]) {
        Object.assign(entry, { reason: entry.slots[0].reason });
      }
      this.sortUnavailable();
    },
    setSingleDate(entry, date) {
      Object.assign(entry, { range: [date, date] });
      this.mergeSameDate(entry);
      this.sortUnavailable();
    },
    // 同一天已有指定時段時，把時段併進去，避免同一天拆成兩筆
    mergeSameDate(entry) {
      const target = this.unavailable.find((u) => u !== entry && u.partial && u.range[0] === entry.range[0]);
      if (!target) {
        return;
      }
      target.slots.push(...entry.slots);
      target.slots.sort(compareSlotStart);
      this.removeUnavailable(entry);
      this.$message({ type: 'info', message: `已併入 ${entry.range[0]} 的指定時段` });
    },
    addOffSlot(entry) {
      const last = entry.slots[entry.slots.length - 1];
      let start = '09:00';
      let end = '12:00';
      if (last && last.end && timeToMinute(last.end) < 1380) {
        const startMin = timeToMinute(last.end);
        start = minuteToTime(startMin);
        end = minuteToTime(Math.min(startMin + 180, 1440));
      }
      entry.slots.push(this.newOffSlot(start, end, last ? last.reason : ''));
    },
    removeOffSlot(entry, slot) {
      Object.assign(entry, { slots: entry.slots.filter((s) => s !== slot) });
    },
    removeUnavailable(entry) {
      this.unavailable = this.unavailable.filter((u) => u !== entry);
    },
    sortUnavailable() {
      this.unavailable = [...this.unavailable].sort(compareUnavailable);
    },
    applyData(data) {
      this.version = data.version;
      this.updatedAt = data.updated_at;
      this.updatedBy = data.updated_by;
      this.holidaysAvailable = !!data.holidays_available;
      this.weekly = WEEKDAYS.map((w) => (data.weekly || [])
        .filter((s) => s.weekday === w)
        .sort((a, b) => timeToMinute(a.start) - timeToMinute(b.start))
        .map((s) => ({
          key: this.nextKey(), start: s.start, end: s.end, branchIds: [...(s.branch_ids || [])],
        })));
      const wholeDays = [];
      const partialByDate = {};
      (data.unavailable || []).forEach((u) => {
        if (!u.start) {
          wholeDays.push({
            key: this.nextKey(),
            range: [u.start_date, u.end_date],
            partial: false,
            reason: u.reason || '',
            slots: [],
          });
          return;
        }
        if (!partialByDate[u.start_date]) {
          partialByDate[u.start_date] = {
            key: this.nextKey(),
            range: [u.start_date, u.start_date],
            partial: true,
            reason: '',
            slots: [],
          };
        }
        partialByDate[u.start_date].slots.push(this.newOffSlot(u.start, u.end || '', u.reason || ''));
      });
      Object.values(partialByDate).forEach((entry) => entry.slots.sort(compareSlotStart));
      this.unavailable = [...wholeDays, ...Object.values(partialByDate)];
      this.sortUnavailable();
      this.loaded = true;
    },
    applyBranches(info) {
      this.branches = (info && info.branches) || [];
      this.registeredBranchIds = (info && info.registeredIds) || [];
      this.branchEditable = !!info;
    },
    async loadData() {
      this.loaded = false;
      await this.$execWithLoadingMessage('讀取中', async () => {
        const [data, branchInfo] = await Promise.all([
          this.loader(),
          this.branchLoader ? this.branchLoader() : null,
        ]);
        this.applyBranches(branchInfo);
        this.applyData(data);
      }, (e) => {
        this.$showAxiosException('載入可排案時段失敗', e);
        this.close(false);
      });
    },
    checkInput() {
      const errs = this.$initInputError();
      WEEKDAYS.forEach((w, idx) => {
        const name = this.weekdayNames[w];
        const slots = this.weekly[idx];
        if (slots.some((s) => !s.start || !s.end)) {
          errs.add(`${name}時段`, '請選擇起訖時間');
          return;
        }
        const sorted = slots
          .map((s) => ({ start: timeToMinute(s.start), end: timeToMinute(s.end) }))
          .sort((a, b) => a.start - b.start);
        if (sorted.some((s) => s.start >= s.end)) {
          errs.add(`${name}時段`, '結束時間必須晚於開始時間');
          return;
        }
        // 相鄰（前一段結束 = 下一段開始）視為連續，只有真正重疊才擋
        if (sorted.some((s, i) => i > 0 && s.start < sorted[i - 1].end)) {
          errs.add(`${name}時段`, '同一天的時段不可重疊');
        }
      });
      if (this.unavailable.some((u) => !u.range || !u.range[0] || !u.range[1])) {
        errs.add('不可排案日期', '請選擇起訖日期');
      } else if (this.unavailable.some((u) => u.range[0] > u.range[1])) {
        errs.add('不可排案日期', '迄日不可早於起日');
      }
      this.unavailable.filter((u) => u.partial).forEach((u) => {
        const name = `${u.range[0]} 不可排案時段`;
        if (u.slots.length === 0) {
          errs.add(name, '請新增時段');
          return;
        }
        if (u.slots.some((s) => !s.start || !s.end)) {
          errs.add(name, '請選擇起訖時間');
          return;
        }
        const sorted = u.slots
          .map((s) => ({ start: timeToMinute(s.start), end: timeToMinute(s.end) }))
          .sort((a, b) => a.start - b.start);
        if (sorted.some((s) => s.start >= s.end)) {
          errs.add(name, '結束時間必須晚於開始時間');
          return;
        }
        // 相鄰（前一段結束 = 下一段開始）視為連續，只有真正重疊才擋
        if (sorted.some((s, i) => i > 0 && s.start < sorted[i - 1].end)) {
          errs.add(name, '同一天的時段不可重疊');
        }
      });
      const reasons = this.unavailable.flatMap((u) => (u.partial ? u.slots.map((s) => s.reason) : [u.reason]));
      if (reasons.some((r) => !r)) {
        errs.add('不可排案原因', '請選擇原因');
      }
      if (errs.hasError) {
        const m = errs.warnMessage;
        this.$showWarn(m.title, m.message);
        return false;
      }
      return true;
    },
    generatePayload() {
      const weekly = [];
      WEEKDAYS.forEach((w, idx) => {
        [...this.weekly[idx]]
          .sort((a, b) => timeToMinute(a.start) - timeToMinute(b.start))
          .forEach((s) => weekly.push({
            weekday: w,
            start: s.start,
            end: s.end,
            branch_ids: [...s.branchIds].sort((a, b) => (this.branchOrder[a] ?? Infinity) - (this.branchOrder[b] ?? Infinity)),
          }));
      });
      // 含過去的資料：過去的不可排案日期原樣送回，避免整份覆寫時被刪除
      const unavailable = [...this.unavailable]
        .sort(compareUnavailable)
        .flatMap((u) => {
          if (!u.partial) {
            return [{
              start_date: u.range[0],
              end_date: u.range[1],
              start: null,
              end: null,
              reason: u.reason,
            }];
          }
          return [...u.slots].sort(compareSlotStart).map((s) => ({
            start_date: u.range[0],
            end_date: u.range[0],
            start: s.start,
            end: s.end,
            reason: s.reason,
          }));
        });
      return {
        weekly,
        unavailable,
        holidays_available: this.holidaysAvailable,
        version: this.version,
      };
    },
    describeConflictLocation(c) {
      if (c.branch_name) {
        return c.room_name ? `${c.branch_name} ${c.room_name}` : c.branch_name;
      }
      return this.$tryMsg(`appointment.locationType.${c.location_type}`) || c.location_type || '';
    },
    async showConflicts(conflicts) {
      const h = this.$createElement;
      const content = h('div', [
        h('p', msg.availability.conflict.appointmentsTitle),
        h('ul', { style: 'max-height: 300px; overflow-y: auto; padding-left: 20px;' }, conflicts.map((c) => {
          const parts = [
            `${this.$timestampToDateTimeMinute(c.start_at)}–${this.$timestampToTime(c.end_at)}`,
            c.user_name,
            this.describeConflictLocation(c),
          ].filter((p) => !!p);
          return h('li', { key: c.id }, parts.join('  '));
        })),
      ]);
      try {
        await this.$alert(content, '預約提醒', { confirmButtonText: '我知道了' });
      } catch (e) {
        // 以關閉鈕離開同樣視為已讀
      }
    },
    async save() {
      if (!this.checkInput()) {
        return;
      }
      await this.$execWithLoadingMessage('儲存中', async () => {
        const rsp = await this.saver(this.generatePayload());
        this.$showSuccess('儲存成功');
        const conflicts = rsp?.conflicts || [];
        if (conflicts.length > 0) {
          await this.showConflicts(conflicts);
        }
        this.close(true);
      }, (e) => {
        if (e?.response?.status === 409) {
          this.$showError(msg.availability.conflict.versionMismatch);
          this.loadData();
          return;
        }
        this.$showAxiosException('儲存失敗', e);
      });
    },
    cancel() {
      this.close(false);
    },
    handleClose() {
      this.close(false);
    },
    close(result) {
      this.$emit('finish', result);
      this.drawer = false;
    },
    reset() {
      this.loaded = false;
      this.version = 0;
      this.updatedAt = null;
      this.updatedBy = null;
      this.holidaysAvailable = false;
      this.weekly = WEEKDAYS.map(() => []);
      this.unavailable = [];
      this.showPast = false;
      this.applyBranches(null);
    },
    show() {
      this.reset();
      this.drawer = true;
      setTimeout(() => this.loadData(), 100);
    },
  },
  mounted() {
    this.$on('show', () => {
      this.show();
    });
  },
};
</script>

<style lang="scss" scoped>
.drawer {
  padding: 20px;
  padding-top: 0;
  overflow-y: auto;
  height: calc(100% - 20px);
}
.title {
  font-weight: bold;
}
.day {
  padding: 8px 0;
  border-bottom: 1px solid #EBEEF5;
  .day-name {
    flex: 0 0 48px;
    line-height: 32px;
  }
  .empty-day {
    line-height: 32px;
  }
  .day-ops {
    flex: 0 0 auto;
  }
}
.time {
  flex: 0 0 120px;
}
.branch {
  flex: 1;
  min-width: 160px;
}
.off {
  padding: 8px 0;
  border-bottom: 1px solid #EBEEF5;
  .range {
    width: 280px;
  }
  .date {
    flex: 0 0 160px;
  }
  .off-slot {
    padding-left: 24px;
  }
  .add-off-slot {
    align-self: flex-start;
    margin-left: 24px;
  }
  .reason {
    flex: 1;
  }
}
.add-off {
  align-self: flex-start;
}
</style>
